// Human triage review (architecture plan §8.11, §8.12, §11.6; build plan P10): the queue of
// runs a person must look at, the run detail a reviewer reads, and the three decisions —
// promote (with edits and recorded overrides of judged checks), reject with a reason, re-run
// with context. Plus the admin's runtime switches: per-category autonomy and the board hold.

import { z } from "zod";
import { AutonomyLevelSchema, RequestStatusSchema } from "./enums";
import { ProposalSchema } from "./proposal";

export const REVIEW_DECISIONS = ["promoted", "rejected", "rerun"] as const;
export const ReviewDecisionSchema = z.enum(REVIEW_DECISIONS);
export type ReviewDecision = z.infer<typeof ReviewDecisionSchema>;

export const TriageQueueQuerySchema = z.object({
  /** `queued_for_human`, `stopped`, or both (default). */
  outcome: z.enum(["queued_for_human", "stopped"]).optional(),
  reason: z.string().optional(),
  category: z.string().optional(),
  /** Reviewed runs are listed only when asked for. */
  reviewed: z.enum(["true", "false"]).optional(),
  cursor: z.string().optional(),
  limit: z.string().optional(),
});
export type TriageQueueQuery = z.infer<typeof TriageQueueQuerySchema>;

export const TriageRunSummarySchema = z.object({
  id: z.uuid(),
  requestId: z.uuid(),
  publicId: z.string(),
  subject: z.string(),
  requestStatus: RequestStatusSchema,
  category: z.string(),
  shape: z.string(),
  outcome: z.string().nullable(),
  queueReason: z.string().nullable(),
  stopReason: z.string().nullable(),
  confidence: z.number().nullable(),
  behaviourSurfaceVersion: z.string(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  /** Since the run finished (or started, if still running), at the time of the read. */
  ageMs: z.number().int().nonnegative(),
  reviewDecision: ReviewDecisionSchema.nullable(),
  reviewedAt: z.iso.datetime().nullable(),
  reviewerDisplayName: z.string().nullable(),
  promotedFromRunId: z.uuid().nullable(),
});
export type TriageRunSummary = z.infer<typeof TriageRunSummarySchema>;

export const TriageQueueSchema = z.object({
  items: z.array(TriageRunSummarySchema),
  nextCursor: z.string().nullable(),
});
export type TriageQueue = z.infer<typeof TriageQueueSchema>;

export const GateCheckViewSchema = z.object({
  id: z.string(),
  passed: z.boolean(),
  /** Present on judged checks; absent on mechanical ones, which cannot be overridden. */
  confidence: z.number().optional(),
  detail: z.string().optional(),
});

export const GateViewSchema = z
  .object({
    passed: z.boolean(),
    checks: z.array(GateCheckViewSchema).default([]),
  })
  .nullable();

export const TriageRunDetailSchema = TriageRunSummarySchema.extend({
  /** The full validated proposal, drafted card included. */
  proposal: z.unknown(),
  /** Exactly what would be, or was, written: what a reviewer reads first. */
  preview: z.array(z.string()),
  agentGate: GateViewSchema,
  applierGate: GateViewSchema,
  duplicateDecision: z.unknown().nullable(),
  citations: z.unknown().nullable(),
  boardSnapshotAt: z.iso.datetime().nullable(),
  epicParentId: z.number().int().nullable(),
  proposedPriority: z.number().int().nullable(),
  reviewEdits: z.unknown().nullable(),
  reviewOverrides: z.unknown().nullable(),
  error: z.string().nullable(),
  request: z.object({
    id: z.uuid(),
    publicId: z.string(),
    subject: z.string(),
    description: z.string(),
    category: z.string(),
    status: RequestStatusSchema,
    onBoard: z.boolean(),
    requesterDisplayName: z.string().nullable(),
    department: z.string(),
    location: z.string(),
    affectedSystemOrDevice: z.string(),
    errorText: z.string(),
    followUpAnswers: z.record(z.string(), z.unknown()),
    createdAt: z.iso.datetime(),
  }),
  priorRuns: z.array(TriageRunSummarySchema),
  /** The run a promotion of this run produced, once the applier has recorded it. */
  promotionRun: TriageRunSummarySchema.nullable(),
});
export type TriageRunDetail = z.infer<typeof TriageRunDetailSchema>;

export const GateOverrideSchema = z
  .object({
    checkId: z.string().min(1),
    reason: z.string().trim().min(5).max(1000),
  })
  .strict();

export const PromoteBodySchema = z
  .object({
    /** The edited proposal; absent means promote the draft as it stands. */
    proposal: z.unknown().optional(),
    /** Judged checks the reviewer overrides, each with a recorded reason. Never mechanical. */
    overrides: z.array(GateOverrideSchema).max(20).default([]),
    /** Release the (edited) requester-safe summary to the thread once the write succeeds. */
    releaseSummary: z.boolean().default(true),
  })
  .strict();
export type PromoteBody = z.infer<typeof PromoteBodySchema>;
export type PromoteBodyInput = z.input<typeof PromoteBodySchema>;

export const RejectBodySchema = z.object({ reason: z.string().trim().min(5).max(2000) }).strict();
export type RejectBody = z.infer<typeof RejectBodySchema>;

export const RerunBodySchema = z.object({ note: z.string().trim().min(5).max(4000) }).strict();
export type RerunBody = z.infer<typeof RerunBodySchema>;

export const ReviewResponseSchema = z.object({
  runId: z.uuid(),
  decision: ReviewDecisionSchema,
  /** The paths of the proposal the reviewer changed (promotion only). */
  changedPaths: z.array(z.string()),
  /** The outbox event that carries the decision onward. */
  eventId: z.uuid().nullable(),
});
export type ReviewResponse = z.infer<typeof ReviewResponseSchema>;

export const TriageCategoryStatsSchema = z.object({
  category: z.string(),
  queued: z.number().int(),
  promoted: z.number().int(),
  promotedWithEdits: z.number().int(),
  rejected: z.number().int(),
  rerun: z.number().int(),
  /** promotedWithEdits / promoted, the §13.2 graduation evidence. Null with no promotions. */
  editRate: z.number().nullable(),
});

export const TriageStatsSchema = z.object({
  queuedTotal: z.number().int(),
  oldestQueuedAgeMs: z.number().int().nullable(),
  byCategory: z.array(TriageCategoryStatsSchema),
});
export type TriageStats = z.infer<typeof TriageStatsSchema>;

/** The parts of a proposal a reviewer may change; identity and provenance fields may not. */
export const PROPOSAL_IMMUTABLE_PATHS = [
  "schemaVersion",
  "requestId",
  "publicId",
  "jobEventId",
  "behaviourSurfaceVersion",
  "category",
  "boardSnapshot",
] as const;

/** Validates an edited proposal against the original: same identity, any content. */
export function validateEditedProposal(
  original: unknown,
  edited: unknown,
):
  | { ok: true; proposal: z.infer<typeof ProposalSchema>; changedPaths: string[] }
  | { ok: false; message: string } {
  const parsed = ProposalSchema.safeParse(edited);
  if (!parsed.success) {
    return {
      ok: false,
      message: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; "),
    };
  }
  const base = ProposalSchema.safeParse(original);
  if (!base.success) return { ok: false, message: "the recorded proposal no longer validates" };
  const o = base.data as unknown as Record<string, unknown>;
  const e = parsed.data as unknown as Record<string, unknown>;
  for (const path of PROPOSAL_IMMUTABLE_PATHS) {
    if (JSON.stringify(o[path]) !== JSON.stringify(e[path])) {
      return { ok: false, message: `${path} cannot be changed at review` };
    }
  }
  return { ok: true, proposal: parsed.data, changedPaths: changedPaths(o, e) };
}

/** Top-level and one level down, enough for the edit-rate metric and an attributable diff. */
export function changedPaths(
  original: Record<string, unknown>,
  edited: Record<string, unknown>,
): string[] {
  const out: string[] = [];
  const keys = new Set([...Object.keys(original), ...Object.keys(edited)]);
  for (const key of keys) {
    const a = original[key];
    const b = edited[key];
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    if (
      a &&
      b &&
      typeof a === "object" &&
      typeof b === "object" &&
      !Array.isArray(a) &&
      !Array.isArray(b)
    ) {
      const inner = new Set([...Object.keys(a), ...Object.keys(b)]);
      for (const k of inner) {
        if (
          JSON.stringify((a as Record<string, unknown>)[k]) !==
          JSON.stringify((b as Record<string, unknown>)[k])
        ) {
          out.push(`${key}.${k}`);
        }
      }
    } else {
      out.push(key);
    }
  }
  return out.sort();
}

// --- Admin runtime settings (§8.12, §11.6) --------------------------------------------------

export const AdminSettingsSchema = z.object({
  autonomy: z.object({
    /** The configured default; every category without an override runs at it. */
    default: AutonomyLevelSchema,
    overrides: z.record(z.string(), AutonomyLevelSchema),
  }),
  boardHold: z.object({
    active: z.boolean(),
    reason: z.string().nullable(),
  }),
  updatedAt: z.iso.datetime().nullable(),
  updatedByDisplayName: z.string().nullable(),
});
export type AdminSettings = z.infer<typeof AdminSettingsSchema>;

export const AutonomyPatchBodySchema = z
  .object({
    category: z.string().min(1).max(100),
    /** Null clears the override, returning the category to the configured default. */
    level: AutonomyLevelSchema.nullable(),
  })
  .strict();
export type AutonomyPatchBody = z.infer<typeof AutonomyPatchBodySchema>;

export const BoardHoldBodySchema = z
  .object({
    active: z.boolean(),
    reason: z.string().trim().max(500).optional(),
  })
  .strict()
  .refine((b) => !b.active || (b.reason && b.reason.length >= 3), {
    message: "a hold needs a reason",
    path: ["reason"],
  });
export type BoardHoldBody = z.infer<typeof BoardHoldBodySchema>;
